import { type ThirdPartyDeviceType } from "../../DeviceTypes/ThirdPartyDevice.js";
import { type SensorSpecifier } from "../../schemas/device/subSchemas/sensorSpecifier.js";
import { type ExternalLinkConfigInfo, type FirstPartyDeviceType, type ManufacturerName } from "../../shared.js";
import {
    type BatteryIndicatorToLabelConversion,
    type BatteryIndicatorToWarningLevelConversion,
} from "../DeviceConfiguration/deviceBatteryHelpers.js";
import { type QRValidationFunction } from "../DeviceConfiguration/deviceQrCodeHelpers.js";
import type { DevicePortConfig } from "../portConfigs.js";

export type DeviceGeneration = "sprout" | "seed" | "sap" | "gen4";
export type ValveType = "block" | "micro" | "none";

export enum DeviceTypeSupportStatus {
    /**
     * Devices that are actively being sold and are fully supported with no caveats
     */
    ACTIVE = "active",
    /**
     * Devices that are no longer being sold and are not expected to be able to function.
     * These devices will not be able to be registered
     */
    RETIRED = "retired",
    /**
     * Devices that are no longer being sold but are still supported.
     * These devices can still be registered, but will only show up if the user is an admin.
     */
    ADMIN_ONLY = "adminOnly",
}

export type DeviceConfigurationProto = {
    friendlyName: string;
    friendlyNameShort: string;
    descriptionText: string;
    validateQRCode: QRValidationFunction;
    iconName: string;
    batteryIndicatorToWarningLevel: BatteryIndicatorToWarningLevelConversion;
    batteryIndicatorToSpecificLabel: BatteryIndicatorToLabelConversion;
    externalLinksConfigInfo: Record<string, ExternalLinkConfigInfo>;
    manufacturerName: ManufacturerName;
    deviceTypeSupportStatus: DeviceTypeSupportStatus;
    valveCount: number;
    valveType: ValveType;
    supportedSensors: number;

    /** Preferred first consonant of the verdiId CVC syllable, biasing IDs by device type
     *  (e.g. "G" → "X-Gok-300"). Single uppercase strong consonant. */
    preferredVerdiIdStart: string;

    /**
     * How long after the last contact (compared to a reference time) before the device is considered
     * offline, used when no LoRa network profile setting applies. Third-party devices always use this
     * (see `ThirdPartyDevice.utilities.getOnlineStatus`); first-party devices normally take the
     * profile-based threshold instead (see the backend's offlineDeviceService).
     */
    fallbackOfflineThresholdMs: number;

    isGateway: boolean;

    /**
     * Indicates this device is a standalone device with a single sensor connection, as opposed to a normal sensor that requires a connecting node
     */
    isStandaloneSingleSensor: boolean;

    /** If true, entity stays visible on the map regardless of selected AOI/field (e.g. gateways). */
    visibleRegardlessOfSelectedAOI?: boolean;
    sensorPortConfiguration?: DevicePortConfig;
};

export type DeviceConfiguration = DeviceConfigurationProto & {
    type: FirstPartyDeviceType;
    isVerdiDevice: true;
    /** @deprecated Superseded by numeric device versioning (DeviceVersion.generation). */
    generation: DeviceGeneration;
};

export type ThirdPartyDeviceConfiguration = DeviceConfigurationProto & {
    type: ThirdPartyDeviceType;
    isVerdiDevice: false;
    /** @deprecated Legacy third-party-only field; superseded by the incoming sensor system. */
    defaultConnectedSensors: SensorSpecifier[];
};

export type DeviceTypeConfiguration = DeviceConfiguration | ThirdPartyDeviceConfiguration;

/**
 * Binds each key to the configuration shape for its side, so a first-party key cannot be given a
 * third-party config (or vice versa) without a type error.
 */
export type DeviceTypeConfigurationTable = {
    [K in FirstPartyDeviceType]: DeviceConfiguration & { type: K };
} & {
    [K in ThirdPartyDeviceType]: ThirdPartyDeviceConfiguration & { type: K };
};
